import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { ExternalChip } from '../../lib/types';
import { mapLookupChipToExternalChip } from '../../lib/chipAdapters';
import { externalChipLookupQuery, ExternalChipLookupData } from '../../lib/rescueQueries';
import { searchResellerChipsQuery } from '@/appview/ListChips/ResellerSearchResults.graphql';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalField } from '../../components/FormField/PortalField';
import { trackPetLookupSearched } from '../../analytics';
import styles from './Chips.module.scss';

interface PetLookupModalProps {
  open: boolean;
  onClose: () => void;
  onViewExternal: (chip: ExternalChip) => void;
  onViewOwn: (graphqlId: string) => void;
}

export default function PetLookupModal({ open, onClose, onViewExternal, onViewOwn }: PetLookupModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExternalChip[] | null>(null);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const [fetchExternalChip] = useLazyQuery<ExternalChipLookupData>(externalChipLookupQuery);
  const [searchResellerChips, { loading: searching }] = useLazyQuery(searchResellerChipsQuery);

  function reset() {
    setQuery('');
    setResults(null);
    setError('');
    setSearched(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSearch() {
    const trimmed = query.trim();
    if (!trimmed) {
      setError('Please enter a chip ID to search.');
      return;
    }
    setError('');

    try {
      // First check if the chip belongs to this reseller
      const resellerResult = await searchResellerChips({
        variables: { input: { searchTerm: trimmed } },
      });

      const ownChips = resellerResult.data?.currentUser?.chipReseller?.searchChips?.chips;

      if (ownChips && ownChips.length > 0) {
        // Found in reseller's own chips
        const chip = ownChips[0];
        const contactInfo = chip.latestContactHistoryEntry?.contactInfo;
        const ownerName = contactInfo
          ? `${contactInfo.firstName}${contactInfo.lastName ? ` ${contactInfo.lastName}` : ''}`
          : null;

        setSearched(true);
        trackPetLookupSearched(true);
        setResults([
          {
            _graphqlId: chip.id,
            isOwn: true,
            id: chip.shortId,
            fullChipId: chip.serialNumber,
            status: 'active',
            assignedTo: ownerName ? 'adopter' : 'rescue',
            petName: chip.pet?.name ?? null,
            species: null,
            breed: chip.pet?.breed?.name ?? null,
            mixedBreed: false,
            gender: null,
            dob: null,
            dobEstimated: false,
            spayedNeutered: null,
            color: null,
            weight: null,
            additionalInfo: null,
            ownerName,
            ownerEmail: null,
            ownerPhone: null,
            ownerStreet: null,
            ownerApt: null,
            ownerCity: null,
            ownerState: null,
            ownerZip: null,
            organization: 'Owned by you',
            registeredDate: '',
            lastUpdated: '',
            history: [],
          },
        ]);
        return;
      }

      // Not found in reseller — try external chip lookup (verified rescue access)
      const { data, error: queryError } = await fetchExternalChip({ variables: { id: trimmed } });
      setSearched(true);
      if (queryError || !data?.resellerChip) {
        trackPetLookupSearched(false);
        setResults([]);
        return;
      }
      trackPetLookupSearched(true);
      setResults([mapLookupChipToExternalChip(data.resellerChip)]);
    } catch {
      setSearched(true);
      trackPetLookupSearched(false);
      setResults([]);
    }
  }

  function handleView(chip: ExternalChip) {
    handleClose();
    if (chip.isOwn) {
      setTimeout(() => onViewOwn(chip._graphqlId), 200);
    } else {
      setTimeout(() => onViewExternal(chip), 200);
    }
  }

  return (
    <Modal open={open} onClose={handleClose} size="extra-wide">
      <ModalTitle>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Pet Lookup
        </span>
      </ModalTitle>
      <p className={styles.modalDescription}>
        Search by chip ID to locate missing or found pets registered with Fi Nano. This searches all chips across all
        organizations.
      </p>

      {!searched && (
        <>
          <PortalField label="Chip ID" error={error} showError={!!error}>
            <PortalInput
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter chip ID (15-digit or short code)"
              hasError={!!error}
              autoFocus
            />
          </PortalField>
          <ModalActions>
            <Button variant="green" onClick={handleSearch} disabled={searching}>
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </ModalActions>
        </>
      )}

      {searched && results && results.length > 0 && (
        <>
          <div className={styles.lookupResults}>
            <table className={styles.lookupTable}>
              <thead>
                <tr>
                  <th>Chip ID</th>
                  <th>Pet</th>
                  <th>Owner</th>
                  <th>Organization</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {results.map((chip) => (
                  <tr key={chip.id}>
                    <td>
                      <div>{chip.id}</div>
                      <div className={styles.lookupSubtext}>{chip.fullChipId}</div>
                    </td>
                    <td>
                      <div>{chip.petName || '\u2014'}</div>
                      <div className={styles.lookupSubtext}>{chip.breed}</div>
                    </td>
                    <td>{chip.ownerName || 'No owner'}</td>
                    <td>{chip.organization || 'External'}</td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => handleView(chip)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ModalActions>
            <Button
              variant="secondary"
              onClick={() => {
                setSearched(false);
                setResults(null);
              }}
            >
              New Search
            </Button>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </ModalActions>
        </>
      )}

      {searched && results && results.length === 0 && (
        <>
          <div className={styles.lookupEmpty}>
            <p>No chips found matching &ldquo;{query}&rdquo;.</p>
            <p className={styles.lookupEmptyHint}>Try searching with the full 15-digit chip ID or the short code.</p>
          </div>
          <ModalActions>
            <Button
              variant="secondary"
              onClick={() => {
                setSearched(false);
                setResults(null);
              }}
            >
              Try Again
            </Button>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </ModalActions>
        </>
      )}
    </Modal>
  );
}
